import { DOMAINS, IMPORTANCE, SECTIONS } from '@repo/monitoring';
import type {
  GutBodyConnectionsOrganConditionRow,
  GutBodyConnectionsOrganMarkerRow,
} from '@repo/types';
import { buildGutBodyConnectionsOrgans } from '@repo/utils';
import { useQueries, useSuspenseQueries } from '@tanstack/react-query';
import { API } from 'api';
import { apiBasePath, apiName } from 'assets/constants';
import { useMemo } from 'react';

const getOrganConditions = async (kitId: string) => {
  const response = await API.get(
    apiName,
    `${apiBasePath}/organs/${kitId}`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.CONDITIONS,
        importance: IMPORTANCE.CRITICAL,
      },
      context: {
        kitId,
      },
    },
  );
  return response.result as GutBodyConnectionsOrganConditionRow[];
};

const getOrgansMarkers = async (kitId: string) => {
  const response = await API.get(
    apiName,
    `${apiBasePath}/organs/${kitId}/markers`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.CONDITIONS,
        importance: IMPORTANCE.CRITICAL,
      },
      context: {
        kitId,
      },
    },
  );
  return response.result as GutBodyConnectionsOrganMarkerRow[];
};

const getOrganConditionsQuery = (kitId?: string) => ({
  queryKey: [apiName, 'getOrganConditions', kitId],
  queryFn: () => (kitId ? getOrganConditions(kitId) : []),
});

const getOrgansMarkersQuery = (kitId?: string) => ({
  queryKey: [apiName, 'getOrgansMarkers', kitId],
  queryFn: () => (kitId ? getOrgansMarkers(kitId) : []),
});

const getOrganQueries = (kitId?: string) =>
  [getOrganConditionsQuery(kitId), getOrgansMarkersQuery(kitId)] as const;

export default function useOrgans(kitId?: string) {
  const [{ data: conditionRows }, { data: markerRows }] = useSuspenseQueries({
    queries: getOrganQueries(kitId),
  });

  const organs = useMemo(
    () => buildGutBodyConnectionsOrgans(conditionRows, markerRows),
    [conditionRows, markerRows],
  );

  return { organs };
}

/** `useOrgans` without suspending: no organs until the conditions have loaded. */
export function useOrgansNonSuspense(kitId?: string) {
  const [{ data: conditionRows }, { data: markerRows }] = useQueries({
    queries: getOrganQueries(kitId),
  });

  const organs = useMemo(
    () => buildGutBodyConnectionsOrgans(conditionRows ?? [], markerRows ?? []),
    [conditionRows, markerRows],
  );

  return { organs };
}
